import React, { useEffect, useState } from 'react';
import resolveImageUrl from '../../utils/imageUtils';
import axios from '../../utils/axios';
import toast from 'react-hot-toast';
import Sidebar from './sidebar';

const Inspection = ({ inspectorId }) => {
  const [properties, setProperties] = useState([]);
  const [explanations, setExplanations] = useState({});
  const [selectedReasons, setSelectedReasons] = useState({});

  const user = JSON.parse(localStorage.getItem('user'));

  const dropdownOptions = [
    '',
    'Property does not match site',
    'Incorrect GPS location',
    'Already verified',
    'Under construction',
    'Other'
  ];

  useEffect(() => {
    const fetchAssigned = async () => {
      try {
        const res = await axios.get(`/properties/assigned/${user.id}`);
        const list = res.data || [];
        const seen = new Set();
        const unique = list.filter((p) => {
          const key = String(p?.id ?? p?.pin ?? "").trim();
          if (!key) return true;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setProperties(unique);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load assigned properties');
      }
    };

    fetchAssigned();
  }, [inspectorId]);

  const handleChange = (propertyId, value) => {
    setExplanations(prev => ({ ...prev, [propertyId]: value }));
  };

  const handleReasonSelect = (propertyId, value) => {
    setSelectedReasons(prev => ({ ...prev, [propertyId]: value }));
    setExplanations(prev => ({
      ...prev,
      [propertyId]: `${value} ${prev[propertyId] || ''}`.trim()
    }));
  };

  const handleSubmit = async (propertyId) => {
    const explanation = explanations[propertyId];
    if (!explanation) {
      toast.error('Explanation cannot be empty');
      return;
    }

    const inspectorId = user.id;

    try {
      await axios.post('/properties/submit-explanation', {
        propertyId,
        inspectorId,
        explanation
      });
      toast.success('Explanation submitted');
      setProperties(prev => prev.filter(p => p.id !== propertyId));
    } catch (err) {
      console.error(err);
      toast.error('Submission failed');
    }
  };

  return (
    <div className="flex flex-col bg-gray-100 min-h-screen">
      <Sidebar />
      {/* Sidebar is fixed at width 64 (16rem), so leave padding on main */}
      <main className="flex-1 pl-64  md:p-8 transition-all ">
        <h2 className="text-3xl font-bold mb-8 text-gray-800">
          Assigned Properties for Explanation
        </h2>

        {properties.length === 0 ? (
          <p className="text-gray-600">No assigned properties</p>
        ) : (
          <div className="grid gap-8 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
            {properties.map((prop) => (
              <div
                key={prop.id}
                className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 flex flex-col hover:shadow-lg transition"
              >
                {/* Property Image */}
                {prop.image ? (
                  <img
                    src={resolveImageUrl(prop.image)}
                    alt="property"
                    className="w-full h-48 object-cover rounded-lg mb-4 border"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-100 rounded-lg mb-4 flex items-center justify-center text-gray-400">
                    No Image Available
                  </div>
                )}

                {/* Details */}
                <div className="text-sm text-gray-800 mb-4 space-y-1">
                  <p><strong>Property ID:</strong> {prop.id}</p>
                  <p><strong>PIN:</strong> {prop.pin}</p>
                  <p><strong>Block:</strong> {prop.block}</p>
                  <p><strong>Area:</strong> {prop.rating_area}</p>
                  <p><strong>Status:</strong> <span className="text-blue-600 font-medium">{prop.status}</span></p>
                  <p><strong>Inspector ID:</strong> {prop.inspector_id}</p>
                  <p><strong>Explanation Status:</strong> <span className="text-yellow-600">{prop.explanation_status}</span></p>
                </div>

                {/* Reason Dropdown */}
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Reason</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={selectedReasons[prop.id] || ''}
                  onChange={(e) => handleReasonSelect(prop.id, e.target.value)}
                >
                  {dropdownOptions.map((reason, idx) => (
                    <option key={idx} value={reason}>
                      {reason || 'Select a reason...'}
                    </option>
                  ))}
                </select>

                {/* Explanation Textarea */}
                <textarea
                  className="w-full text-sm p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
                  rows={4}
                  placeholder="Write your explanation here..."
                  value={explanations[prop.id] || ''}
                  onChange={(e) => handleChange(prop.id, e.target.value)}
                />

                {/* Submit Button */}
                <button
                  className="mt-4 w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-2 px-4 rounded-xl shadow-md transition-all duration-200"
                  onClick={() => handleSubmit(prop.id)}
                >
                  Submit Explanation
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Inspection;
